import { FormsModule } from "@angular/forms";
import { Component, computed, input, OnInit, output, signal } from "@angular/core";
import { DrawerModule } from "primeng/drawer";
import { RadioButtonModule } from "primeng/radiobutton";
import { AddAreaDialogComponent, AddAreaSubmit } from "../../../../../../../domains/jobs/components/job-areas/add-area/add-area-dialog.component";
import { EditRoomNameArea, EditRoomNamesComponent, EditRoomNameUpdate } from "../../../../../../../domains/jobs/components/job-areas/edit-rooms/edit-room-names.component";
import { LocationsAreaCardComponent, NaStatusChangeEvent } from "../locations-area-card/locations-area-card.component";
import { Area, Assertion, Zone } from "../../../../../../../core/database/db";
import { AreaWithDetails, BqInspectionDataService } from "../../../../../../../domains/jobs/services/bq-inspection-data.service";
import { BqStepDataServiceFactory } from "../../../../../../shared/bq-step-data-service/bq-step-data-service-factory";
import { LoggerService } from "../../../../../../../shared/services/logger.service";
import { ButtonModule } from "primeng/button";
import { UiToastService } from "../../../../../../../shared/components/toast/ui-toast.service";

export enum LocationAction {
  Viewing = "Viewing",
  MarkAsNA = "MarkAsNA",
  RemoveNA = "RemoveNA",
}

export interface SaveNAPayload {
  updatedAreas: Area[];
  updatedAssertions: Assertion[];
  areaQueue: { key: number; changes: object }[];
  assertionQueue: { key: number; changes: object }[];
}

const UNSELECTED_ZONE = -1;

@Component({
  selector: "locations-tab",
  imports: [FormsModule, RadioButtonModule, DrawerModule, AddAreaDialogComponent, EditRoomNamesComponent, LocationsAreaCardComponent, ButtonModule],
  templateUrl: "./locations-tab.component.html",
  styleUrl: "./locations-tab.component.scss"
})
export class LocationsTabComponent implements OnInit {

  constructor(
    private logger: LoggerService,
    public bqStepDataServiceFactory: BqStepDataServiceFactory,
    public toastService: UiToastService,
  ) { }

  areasWithDetails = input.required<AreaWithDetails[]>();
  bqStep = input.required<string>();
  isEditingChange = output<boolean>();

  areasMarkedForNaChange = signal<Record<number, boolean>>({});
  currentAction = signal<LocationAction>(LocationAction.Viewing);
  isActionMenuVisible = signal(false);
  selectedZone = signal(UNSELECTED_ZONE);
  selectedZoneName = signal("");
  addingArea = signal(false);
  editingRoomNames = signal(false);
  newAreaID = signal(-1);
  isSavingAreaNaChanges = signal(false);
  isSavingAreaUpdates = signal(false);

  private bqStepDataService = signal<BqInspectionDataService | null>(null);
  readonly LocationAction = LocationAction;
  readonly UNSELECTED_ZONE = UNSELECTED_ZONE;

  zones = computed(() => this.bqStepDataService()?.zones() ?? []);
  components = computed(() => this.bqStepDataService()?.components() ?? []);
  areasConfig = computed(() => this.bqStepDataService()?.areasConfig());
  job = computed(() => this.bqStepDataService()?.job());
  isEditing = computed(() => this.currentAction() !== LocationAction.Viewing);

  areasByZone = computed(() =>
    this.areasWithDetails()
      .filter(a => {
        let shouldShowArea = true;

        const hasSelectedZone = this.selectedZone() !== UNSELECTED_ZONE;

        if (this.isEditing() && hasSelectedZone) {
          const currentZoneIsSelected = a.zoneId === this.selectedZone();

          if (this.currentAction() === LocationAction.MarkAsNA) {
            shouldShowArea = currentZoneIsSelected && !!a.isApplicable && !a.started;
          } else if (this.currentAction() === LocationAction.RemoveNA) {
            shouldShowArea = currentZoneIsSelected && !a.isApplicable;
          }
        }

        return shouldShowArea;
      })
      .reduce((areasByZone, areaWithDetails) => {

        if (!!areasByZone.has(areaWithDetails.zone) === false) {
          areasByZone.set(areaWithDetails.zone, []);
        }

        areasByZone.get(areaWithDetails.zone)!.push(areaWithDetails);

        return areasByZone;
      }, new Map<Zone, AreaWithDetails[]>())
  );

  hasAreasMarkedForNaChange = computed(() => Object.keys(this.areasMarkedForNaChange()).length > 0);

  editableAreas = computed<EditRoomNameArea[]>(() =>
    this.areasWithDetails().map(area => ({
      id: area.id!,
      zoneId: area.zoneId!,
      name: area.name,
      sortingOrder: area.sortingOrder,
    }))
  );

  naRoomTextByZone = computed(() => {
    const byZone = this.areasByZone();
    return (zone: Zone) => {
      const areas = byZone.get(zone) ?? [];
      const naAreas = areas.filter(a => !a.isApplicable);

      let text = "";
      if (this.isEditing() === false && naAreas.length > 0) {
        const names = naAreas.map(a => a.name);
        const last = names.pop()!;
        text = `Rooms marked as N/A: ${names.length > 0 ? `${names.join(", ")} & ${last}` : last}`;
      }

      return text;
    };
  });

  canMarkAreaAsNa = computed(() => {
    return this.areasWithDetails().some(area => area.zoneId === this.selectedZone() && area.isApplicable && !area.started);
  });

  canRemoveAreaFromNa = computed(() => {
    return this.areasWithDetails().some(area => area.zoneId === this.selectedZone() && !area.isApplicable);
  });

  ngOnInit(): void {
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionDataService>(this.bqStep()));
  }

  openActionMenu(zoneId: number, zoneName: string): void {
    this.isActionMenuVisible.set(true);
    this.selectedZone.set(zoneId);
    this.selectedZoneName.set(zoneName);
  }

  openAddAreaDialog(): void {
    this.isActionMenuVisible.set(false);
    this.addingArea.set(true);
  }

  onAddAreaVisibleChange(visible: boolean): void {
    this.addingArea.set(visible);
    if (!visible) {
      this.selectedZone.set(UNSELECTED_ZONE);
    }
  }

  openEditRoomNameDialog(): void {
    this.isActionMenuVisible.set(false);
    this.editingRoomNames.set(true);
  }

  onEditRoomNamesVisibleChange(visible: boolean): void {
    this.editingRoomNames.set(visible);
    if (!visible) {
      this.selectedZone.set(UNSELECTED_ZONE);
    }
  }

  async saveEditedRoomNames(updates: EditRoomNameUpdate[]): Promise<void> {
    this.isSavingAreaUpdates.set(true);

    if (!updates.length) {
      this.onEditRoomNamesVisibleChange(false);
      return;
    }

    const validUpdates = updates.reduce((list, update) => {
      if (!update.id) {
        this.logger.error(`[LocationsTabComponent:saveEditedRoomNames] Missing area id for edited room name "${update.name}". This room name will not be saved.`);
        return list;
      }
      list.push({ id: update.id, name: update.name.trim() });
      return list;
    }, [] as EditRoomNameUpdate[]);

    if (!validUpdates.length) {
      this.onEditRoomNamesVisibleChange(false);
      return;
    }

    try {
      await this.bqStepDataService()!.updateAreaNames(validUpdates);
    } catch (error) {
      this.logger.error("[LocationsTabComponent:saveEditedRoomNames] Failed to save edited room names.", error);
      throw error;
    }

    this.isSavingAreaUpdates.set(false);
    this.onEditRoomNamesVisibleChange(false);
  }

  async onAddArea(event: AddAreaSubmit): Promise<void> {

    this.isSavingAreaUpdates.set(true);

    const newAreaId = await this.bqStepDataService()!.addNewAreaToZone(event, this.selectedZone());
    if (!!newAreaId === false) {
      this.logger.error("[LocationsTabComponent:onAddArea] Failed to add new area to zone.");
      this.toastService.error("Failed to add new area. Please try again.");
      return;
    }

    this.isSavingAreaUpdates.set(false);

    this.addingArea.set(false);
    this.selectedZone.set(UNSELECTED_ZONE);

    setTimeout(() => {
      document.getElementById(`area-${newAreaId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      this.newAreaID.set(newAreaId);
      setTimeout(() => this.newAreaID.set(-1), 2500);
    }, 1);
  }

  setMarkAsNaMode(): void {
    this.currentAction.set(LocationAction.MarkAsNA);
    this.isActionMenuVisible.set(false);
    this.isEditingChange.emit(true);
  }

  setRemoveNaMode(): void {
    this.currentAction.set(LocationAction.RemoveNA);
    this.isActionMenuVisible.set(false);
    this.isEditingChange.emit(true);
  }

  cancelMarkingNa(): void {
    this.currentAction.set(LocationAction.Viewing);
    this.areasMarkedForNaChange.set({});
    this.selectedZone.set(UNSELECTED_ZONE);
    this.isEditingChange.emit(false);
  }

  async saveAreaNaChanges(): Promise<void> {
    this.isSavingAreaNaChanges.set(true);

    const currentSavingAction = this.currentAction();
    const areMarkedAreaIdsApplicable = currentSavingAction === LocationAction.RemoveNA; // else we are marking N/A, so value should be false
    const markedAreaIds = Object.keys(this.areasMarkedForNaChange()).map(id => Number.parseInt(id));

    await this.bqStepDataService()!.updateAreasNaStatus(markedAreaIds, areMarkedAreaIdsApplicable);

    this.isSavingAreaNaChanges.set(false);

    this.cancelMarkingNa();
  }

  onToggleNaStatus(event: NaStatusChangeEvent): void {
    const { areaId, isNa } = event;

    this.areasMarkedForNaChange.update(current => {
      const updated = { ...current };
      if (isNa) {
        updated[areaId] = true;
      } else {
        delete updated[areaId];
      }
      return updated;
    });
  }
}

